import React from 'react'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import { useDndScrolling } from 'react-dnd-scrolling'
import { Add } from 'iconsax-react'

import CustomFieldOption from '../../CustomFieldOption'
import Button from '@SonarCX/components/atoms/Button2'

import useBaseStyles from '../styles'
import useStyles from './styles'

type MultioptionEntryProps = {
  state: APICustomField
  setState: React.Dispatch<React.SetStateAction<APICustomField>>
}

const MultioptionEntry: React.FC<MultioptionEntryProps> = ({
  state,
  setState
}) => {
  const isLegacyField = !!state.options
  const ref = React.useRef(null)
  useDndScrolling(ref, {})
  const [newOption, setNewOption] = React.useState('')

  const baseClasses = useBaseStyles()
  const classes = useStyles()

  React.useEffect(() => {
    if (isLegacyField) return

    //migrated fields dont have conditions, add them.
    setState(old => {
      const toUpdate = cloneDeep(old)
      const mappedOptions = toUpdate.config.options.map(opt => ({
        ...opt,
        conditions: [...(opt.conditions ?? [])]
      }))
      toUpdate.config.options = mappedOptions
      return toUpdate
    })
  }, [isLegacyField, setState])

  const handleMoveOption = React.useCallback(
    (start: string, end: string) => {
      if (start === end) {
        return
      }

      setState(old => {
        const oldItems = cloneDeep(old)

        if (isLegacyField) {
          const oldIdx = oldItems.options.findIndex(v => v === start)
          const newIdx = oldItems.options.findIndex(v => v === end)

          const [removed] = oldItems.options.splice(oldIdx, 1)
          oldItems.options.splice(newIdx, 0, removed)
          return oldItems
        }
        // Calculate indexes
        const oldIdx = oldItems.config.options.findIndex(v => v.val === start)
        const newIdx = oldItems.config.options.findIndex(v => v.val === end)

        const [removed] = oldItems.config.options.splice(oldIdx, 1)
        oldItems.config.options.splice(newIdx, 0, removed)

        return oldItems
      })
    },
    [isLegacyField, setState]
  )

  const handleRemoveOption = React.useCallback(
    (value: string) => {
      setState(old => {
        const oldItems = cloneDeep(old)
        if (isLegacyField) {
          const oldIdx = oldItems.options.findIndex(v => v === value)
          oldItems.options.splice(oldIdx, 1)
          return oldItems
        }

        const oldIdx = oldItems.config.options.findIndex(v => v.val === value)
        oldItems.config.options.splice(oldIdx, 1)
        return oldItems
      })
    },
    [isLegacyField, setState]
  )

  const handleAdd = React.useCallback(() => {
    if (newOption.trim() === '') {
      return
    }

    // If option already exists, dont add it
    if (isLegacyField) {
      if (state.options.some(opts => opts === newOption)) {
        return
      }
    } else {
      if (state.config.options.some(opts => opts.val === newOption)) {
        return
      }
    }

    setState(old => {
      const updated = cloneDeep(old)
      if (isLegacyField) {
        updated.options.push(newOption)
      } else {
        updated.config.options.push({ val: newOption, conditions: [] })
      }
      return updated
    })

    setNewOption('')
  }, [isLegacyField, newOption, state, setState])

  const handleOnKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleAdd()
      }
    },
    [handleAdd]
  )

  const handleAddCondition = React.useCallback(
    (id, condition) => {
      setState(old => {
        const updated = cloneDeep(old)

        ;(updated.config as APICustomFieldConfigOptions).options
          .find(v => v.val === id)
          .conditions.push(condition)

        return updated
      })
    },
    [setState]
  )

  const handleRemoveCondition = React.useCallback(
    (value, index) => {
      setState(old => {
        const updated = cloneDeep(old)

        ;(updated.config as APICustomFieldConfigOptions).options
          .find(v => v.val === value)
          .conditions.splice(index, 1)

        return updated
      })
    },
    [setState]
  )

  const handleUpdateCondition = React.useCallback(
    (id, index, condition) => {
      setState(old => {
        const updated = cloneDeep(old)

        ;(updated.config as APICustomFieldConfigOptions).options.find(
          v => v.val === id
        ).conditions[index] = condition

        return updated
      })
    },
    [setState]
  )

  const handleRenameOption = React.useCallback(
    (id, newId) => {
      if (newId.trim() === '') {
        return
      }

      // If option already exists, dont add it
      if (isLegacyField) {
        if (state.options.some(opts => opts === newId)) {
          return
        }
      } else {
        if (state.config.options.some(opts => opts.val === newId)) {
          return
        }
      }

      setState(old => {
        const updated = cloneDeep(old)

        const updatedOpts = updated.config.options.map(v => {
          if (v.val === id) {
            return { ...v, val: newId }
          }
          return v
        })
        updated.config.options = updatedOpts
        return updated
      })
    },
    [isLegacyField, setState, state]
  )
  return (
    <div className={clsx(baseClasses.row, baseClasses.column)}>
      <div className={baseClasses.label}>Options</div>

      <div ref={ref} className={baseClasses.scrollable}>
        {isLegacyField
          ? state.options.map((opt, i) => (
              <CustomFieldOption
                key={`${opt}-${i}`}
                id={opt}
                handleMoveOption={handleMoveOption}
                handleRemoveOption={handleRemoveOption}
                isLast={i === state.options.length - 1}
              />
            ))
          : state.config.options.map((opt, i) => (
              <CustomFieldOption
                key={`${opt.val}-${i}`}
                id={opt.val}
                option={opt}
                handleMoveOption={handleMoveOption}
                handleRemoveOption={handleRemoveOption}
                handleAddCondition={handleAddCondition}
                handleUpdateCondition={handleUpdateCondition}
                handleRemoveCondition={handleRemoveCondition}
                handleRenameOption={handleRenameOption}
                type={state.object_type}
                isLast={i === state.config.options.length - 1}
                editing={state.id}
                showConditions
                enableNameEdit
              />
            ))}
      </div>

      <div className={classes.inputArea}>
        <input
          placeholder='New option'
          maxLength={50}
          value={newOption}
          onChange={e => setNewOption(e.target.value)}
          onKeyDown={handleOnKeyDown}
          className={clsx(baseClasses.input, classes.input)}
        />

        <Button
          disabled={newOption.trim().length === 0}
          className={classes.button}
        >
          <Add variant='Linear' onClick={handleAdd} />
        </Button>
      </div>
    </div>
  )
}

export default MultioptionEntry
